import { action, set, setProperties } from '@ember/object';
import Route from '@ember/routing/route';
import type RouteInfo from '@ember/routing/route-info';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { all, type Task, task } from 'ember-concurrency';
import type { Insightpagesfavourite } from 'frontend-utils/models';
import {
  RI_BREAKDOWNS,
  type RiBreakdown,
} from 'frontend-utils/utils/constants/constant-values';
import {
  type DemoModeObject,
  getDemoModeCookie,
} from 'frontend-utils/utils/hash';
import cloneDeep from 'lodash.clonedeep';
import type HotelRevenueInsightController from 'lumen/controllers/hotel/revenue-insight';
import type {
  Day,
  Period,
  RevenueInsightBreakdownsRequest,
  RevenueInsightKpisRequest,
  RevenueInsightSegmentationValue,
  ReviExchangeRate,
} from 'lumen/models';
import type { SegmentationValueLike } from 'lumen/models/revenue-insight/segmentation-value';
import type { ReviApiResponseLike } from 'lumen/models/revi-api-response';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { Transition } from 'lumen/services/transition';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import {
  INVALID_RESERVATIONS_MESSAGE,
  KPI_CATEGORIES,
  type KpiCategory,
  type ReferenceOption,
  type RevenueTab,
  type RoomsTab,
  type ValidKpi,
} from 'lumen/utils/constants/ri-constant-values';
import { formatUrl } from 'lumen/utils/rates-request';
import getDateRange from 'lumen/utils/revenue-insight/get-date-range';
import getExtractDate from 'lumen/utils/revenue-insight/get-extract-date';
import isRatesAvailable from 'lumen/utils/revenue-insight/rates-data-checks';
import { isAPIReference } from 'lumen/utils/revenue-insight/ri-utils';
import handleRequestError from 'lumen/utils/revenue-insight/route-utils';
import { segmentationValueFormatter } from 'lumen/utils/revenue-insight/segmentation-value-formatter';
import {
  addDummyElementToFilteredSegmentationDict,
  getSegmentationIdValidations,
  type SegmentationDict,
} from 'lumen/utils/routes/hotel/revenue-insight';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

type StoredUnfilteredResponse = {
  cachedResponse: ReviApiResponseLike;
  /**
   * Only set in multi property
   */
  exchangeRates?: ReviExchangeRate[];
  hotelInfoId: string;
};

function areQueryParamsEqual(
  from: Partial<Params>,
  to: Partial<Params>,
): boolean {
  const keysToCheck = [
    'currency',
    'fromDate',
    'showPickupRealised',
    'hidePickupLY',
    'kpiCategory',
    'reference',
    'refFromDate',
    'stayDateFilter',
    'toDate',
    'profile',
  ] as const;

  let areEqual = true;

  keysToCheck.forEach((key) => {
    if (from[key] !== to[key]) {
      areEqual = false;
      return;
    }
  });

  return areEqual;
}

type HasBudetForecastTask = Task<
  {
    hasBudgetData: boolean;
    hasForecastData: boolean;
  },
  [Params]
>;

type Model = {
  breakdowns: Task<ReviApiResponseLike, [DataTaskParams]>;
  events: Task<Day[], [Record<string, unknown>]>;
  favourites: Task<void, []>;
  hasBudgetForecastTask: HasBudetForecastTask;
  holidays: Task<Day[], [Record<string, unknown>]>;
  kpis: Task<ReviApiResponseLike, [DataTaskParams]>;
  pace: Task<ReviApiResponseLike, [DataTaskParams]>;
  pickup: Task<ReviApiResponseLike, [DataTaskParams]>;
  rates: Task<Period[], [Params]>;
};

export type Params = {
  cancellationsView: string;
  currency: string;
  disableDataContinuity: boolean;
  extractDate?: string;
  fromDate: string | null;
  hidePickupLY: boolean;
  highlight: string | undefined;
  kpi: ValidKpi;
  kpiCategory: KpiCategory;
  leadtime: number;
  profile: string | null;
  refFromDate: string | null;
  reference: ReferenceOption;
  revenueTab: RevenueTab;
  roomsTab: RoomsTab;
  scheduleReport: boolean;
  segmentationFilters: string;
  selectedView: string;
  showPickupRealised: boolean;
  stayDateAggregation: string;
  stayDateFilter: string;
  toDate: string | null;
};

type DataTaskParams = {
  filteredQueryParams: Record<string, unknown>;
  params: Params;
  unfilteredQueryParams: Record<string, unknown>;
};

const SEGMENTATION_TYPES = {
  KPI: 'kpi',
  BREAKDOWN: 'breakdown',
  PACE: 'pace',
  PICKUP: 'pickup',
} as const;

type SegmentationType =
  (typeof SEGMENTATION_TYPES)[keyof typeof SEGMENTATION_TYPES];

/**
 * To get context of how this route is implemented, see the documentation in:
 * https://lighthouseintelligence.atlassian.net/wiki/spaces/RI/pages/5456330769/Legacy+Current+RevI+Data+Fetching+in+Frontend
 */
export default class HotelRevenueInsightRoute extends Route {
  queryParams = {
    cancellationsView: {
      refreshModel: false,
    },
    currency: {
      refreshModel: true,
    },
    disableDataContinuity: {
      refreshModel: false,
    },
    extractDate: {
      refreshModel: true,
    },
    fromDate: {
      refreshModel: true,
    },
    showPickupRealised: {
      refreshModel: false,
    },
    hidePickupLY: {
      refreshModel: true,
    },
    kpi: {
      refreshModel: false,
    },
    kpiCategory: {
      refreshModel: true,
    },
    leadtime: {
      // Pace-only refetch (see refetchPace); only the pace endpoint uses leadtime.
      refreshModel: false,
    },
    profile: {
      refreshModel: true,
    },
    refFromDate: {
      refreshModel: true,
    },
    reference: {
      refreshModel: true,
    },
    revenueTab: {
      refreshModel: false,
    },
    roomsTab: {
      refreshModel: false,
    },
    scheduleReport: {
      refreshModel: false,
    },
    segmentationFilters: {
      refreshModel: true,
    },
    selectedView: {
      refreshModel: false,
    },
    stayDateAggregation: {
      refreshModel: false, // Since we do the aggregation in the frontend, the model doesn't need to be refreshed
    },
    stayDateFilter: {
      refreshModel: true,
    },
    toDate: {
      refreshModel: true,
    },
    highlight: {
      refreshModel: false,
    },
  };

  @service declare access: Services['access'];
  @service declare ajax: Services['ajax'];
  // eslint-disable-next-line ember/no-unused-services
  @service declare alerting: Services['alerting'];
  @service declare currencyConversion: Services['currency-conversion'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare riSelections: Services['ri-selections'];
  @service declare router: Services['router'];

  @service declare store: Services['store'];
  @service declare toast: Services['toast'];
  @service declare transition: Services['transition'];

  @tracked favourites: Insightpagesfavourite[] | null = null;
  @tracked storedBreakdownUnfilteredResponse: StoredUnfilteredResponse | null =
    null;
  @tracked storedKpiUnfilteredResponse: StoredUnfilteredResponse | null = null;
  @tracked storedPaceUnfilteredResponse: StoredUnfilteredResponse | null = null;
  @tracked storedPickupUnfilteredResponse: StoredUnfilteredResponse | null =
    null;

  hasAbortedDemoTransition = false;
  lastActiveHotelId: string | null = null;
  storedRatesData: Period[] | null = null;
  storedUnfilteredSegmentationDict: Record<SegmentationType, SegmentationDict> =
    {
      kpi: {},
      breakdown: {},
      pace: {},
      pickup: {},
    };

  unfilteredRequestHadBeenMappedBefore: Record<SegmentationType, boolean> = {
    kpi: false,
    breakdown: false,
    pace: false,
    pickup: false,
  };

  fetchBreakdownDataTask = task(
    async (queryParams: Record<string, unknown>) => {
      const breakdowns =
        await this.store.queryRecord<RevenueInsightBreakdownsRequest>(
          'revenue-insight/breakdowns-request',
          {
            ...queryParams,
            typeKey: 'revenueinsight/breakdowns',
          },
        );

      // Combine the results into a single object with the same format as the old response
      const result: Partial<ReviApiResponseLike> = {};

      // @ts-expect-error: 'breakdowns' is possibly 'null'.
      breakdowns.keys.forEach((key) => {
        // @ts-expect-error: 'breakdowns' is possibly 'null'.
        result[key] = [...breakdowns[key]];
      });

      result.keys = Object.keys(result) as RiBreakdown[];

      return result as ReviApiResponseLike;
    },
  );

  fetchKpiDataTask = task(async (queryParams: Record<string, unknown>) => {
    const kpis = await this.store.queryRecord<RevenueInsightKpisRequest>(
      'revenue-insight/kpis-request',
      {
        ...queryParams,
        typeKey: 'revenueinsight/kpis',
      },
    );
    const result: Partial<ReviApiResponseLike> = {
      // @ts-expect-error: 'kpis' is possibly 'null'.
      all: [kpis.all],
      // @ts-expect-error: 'kpis' is possibly 'null'.
      impact_date: [...kpis.impact_date],
    };
    result.keys = Object.keys(result) as RiBreakdown[];

    return result as ReviApiResponseLike;
  });

  fetchPaceDataTask = task(async (queryParams: Record<string, unknown>) => {
    const pace = await this.store.query<RevenueInsightSegmentationValue>(
      'revenue-insight/segmentation-value',
      {
        ...queryParams,
        typeKey: 'revenueinsight/pace',
      },
    );
    const result: Partial<ReviApiResponseLike> = {
      pace: [...pace],
    };

    result.keys = Object.keys(result) as RiBreakdown[];

    return result as ReviApiResponseLike;
  });

  fetchPickupDataTask = task(async (queryParams: Record<string, unknown>) => {
    const pickup = await this.store.query<RevenueInsightSegmentationValue>(
      'revenue-insight/segmentation-value',
      {
        ...queryParams,
        typeKey: 'revenueinsight/pickup',
      },
    );
    const result: Partial<ReviApiResponseLike> = {
      pickup: [...pickup],
    };

    result.keys = Object.keys(result) as RiBreakdown[];

    return result as ReviApiResponseLike;
  });

  getBreakdownDataTask = task(
    { restartable: true },
    async ({
      params,
      unfilteredQueryParams,
      filteredQueryParams,
    }: DataTaskParams) => {
      try {
        const currentHotelId = this.hotelAndMarketService.hotel.hotelinfo.id;
        // Check if cached response exists and belongs to the currently selected hotel
        const isCacheValid =
          Boolean(this.storedBreakdownUnfilteredResponse?.cachedResponse) &&
          this.storedBreakdownUnfilteredResponse?.hotelInfoId ===
            currentHotelId;

        // Fetch unfiltered request if needed
        const unfilteredRequest =
          isCacheValid && this.storedBreakdownUnfilteredResponse
            ? this.storedBreakdownUnfilteredResponse.cachedResponse
            : this.fetchBreakdownDataTask.perform(unfilteredQueryParams);

        // Fetch filtered request if needed
        const filteredRequest = params.segmentationFilters.length
          ? this.fetchBreakdownDataTask.perform(filteredQueryParams)
          : undefined;

        const [unfilteredResponse, filteredResponse] = await all([
          unfilteredRequest,
          filteredRequest,
        ]);

        // Store unfiltered response if needed
        if (!isCacheValid) {
          this.storedBreakdownUnfilteredResponse = {
            hotelInfoId: this.hotelAndMarketService.hotel.hotelinfo.id!,
            cachedResponse: unfilteredResponse,
          };
        }

        return this.handleUnfilteredAndFilteredRequest({
          unfilteredRequest: unfilteredResponse,
          filteredRequest: filteredResponse,
          isCached: isCacheValid,
          segmentationType: SEGMENTATION_TYPES.BREAKDOWN,
        }) as ReviApiResponseLike;
      } catch (error) {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        handleRequestError(error, params, this);
        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  getEventsDataTask = task(
    { restartable: true },
    async (eventParams: Record<string, unknown>) => {
      if (!this.access.hasEvents) {
        return [];
      }
      const queryParams = cloneDeep(eventParams);
      queryParams['typeKey'] = 'events/accepted';

      try {
        return await this.store.query<Day>(
          'day',
          // @ts-expect-error: Incorrect type
          queryParams,
        );
      } catch (error) {
        this.errorTracking.captureMessage('Events data fetch in RI failed!', {
          error,
        });
        // Return empty response in case of error
        return [];
      }
    },
  );

  getFavouritesDataTask = task(async (): Promise<void> => {
    if (!this.favourites) {
      const queryParams = {
        hotel: this.hotelAndMarketService.hotel?.id,
        page_type: 'RI',
      };

      try {
        const content = await this.store.query<Insightpagesfavourite>(
          'insightpagesfavourite',
          queryParams,
        );
        this.favourites = [...content];
      } catch (error) {
        this.errorTracking.captureMessage(
          'Favourites data fetch in RI failed!',
          {
            error,
          },
        );
      }
    }
  });

  getHolidaysDataTask = task(
    { restartable: true },
    async (eventParams: Record<string, unknown>) => {
      if (!this.access.hasEvents) {
        return [];
      }
      const queryParams = cloneDeep(eventParams);
      queryParams['typeKey'] = 'holidays';

      try {
        return await this.store.query<Day>(
          'day',
          // @ts-expect-error: Incorrect type
          queryParams,
        );
      } catch (error) {
        this.errorTracking.captureMessage('Holidays data fetch in RI failed!', {
          error,
        });
        // Return empty response in case of error
        return [];
      }
    },
  );

  getKpiDataTask = task(
    { restartable: true },
    async ({
      params,
      unfilteredQueryParams,
      filteredQueryParams,
    }: DataTaskParams): Promise<ReviApiResponseLike> => {
      try {
        const currentHotelId = this.hotelAndMarketService.hotel.hotelinfo.id;
        // Check if cached response exists and belongs to the currently selected hotel
        const isCacheValid =
          Boolean(this.storedKpiUnfilteredResponse?.cachedResponse) &&
          this.storedKpiUnfilteredResponse?.hotelInfoId === currentHotelId;

        // Fetch unfiltered request if needed
        const unfilteredRequest =
          isCacheValid && this.storedKpiUnfilteredResponse
            ? this.storedKpiUnfilteredResponse.cachedResponse
            : this.fetchKpiDataTask.perform(unfilteredQueryParams);

        // Fetch filtered request if needed
        const filteredRequest = params.segmentationFilters.length
          ? this.fetchKpiDataTask.perform(filteredQueryParams)
          : undefined;

        const [unfilteredResponse, filteredResponse] = await all([
          unfilteredRequest,
          filteredRequest,
        ]);

        // Store unfiltered response if needed
        if (!isCacheValid) {
          this.storedKpiUnfilteredResponse = {
            hotelInfoId: this.hotelAndMarketService.hotel.hotelinfo.id!,
            cachedResponse: unfilteredResponse,
          };
        }

        const requestData = filteredResponse ?? unfilteredResponse;
        this.setRiSelectionsPropertiesFromRequest(requestData);
        this.validateKpiCategory(params.kpiCategory);

        return this.handleUnfilteredAndFilteredRequest({
          unfilteredRequest: unfilteredResponse,
          filteredRequest: filteredResponse,
          isCached: isCacheValid,
          segmentationType: SEGMENTATION_TYPES.KPI,
        }) as ReviApiResponseLike;
      } catch (error) {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        handleRequestError(error, params, this);
        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  getPaceDataTask = task(
    { restartable: true },
    async ({
      params,
      unfilteredQueryParams,
      filteredQueryParams,
    }: DataTaskParams) => {
      try {
        const currentHotelId = this.hotelAndMarketService.hotel.hotelinfo.id;
        // Check if cached response exists and belongs to the currently selected hotel
        const isCacheValid =
          Boolean(this.storedPaceUnfilteredResponse?.cachedResponse) &&
          this.storedPaceUnfilteredResponse?.hotelInfoId === currentHotelId;

        // leadtime applies only to the pace request, so merge it in here.
        const paceUnfilteredQueryParams = {
          ...unfilteredQueryParams,
          leadtime: params.leadtime,
        };
        const paceFilteredQueryParams = {
          ...filteredQueryParams,
          leadtime: params.leadtime,
        };

        // Fetch unfiltered request if needed
        const unfilteredRequest =
          isCacheValid && this.storedPaceUnfilteredResponse
            ? this.storedPaceUnfilteredResponse.cachedResponse
            : this.fetchPaceDataTask.perform(paceUnfilteredQueryParams);

        // Fetch filtered request if needed
        const filteredRequest = params.segmentationFilters.length
          ? this.fetchPaceDataTask.perform(paceFilteredQueryParams)
          : undefined;

        const [unfilteredResponse, filteredResponse] = await all([
          unfilteredRequest,
          filteredRequest,
        ]);

        // Store unfiltered response if needed
        if (!isCacheValid) {
          this.storedPaceUnfilteredResponse = {
            hotelInfoId: this.hotelAndMarketService.hotel.hotelinfo.id!,
            cachedResponse: unfilteredResponse,
          };
        }

        return this.handleUnfilteredAndFilteredRequest({
          unfilteredRequest: unfilteredResponse,
          filteredRequest: filteredResponse,
          isCached: isCacheValid,
          segmentationType: SEGMENTATION_TYPES.PACE,
        }) as ReviApiResponseLike;
      } catch (error) {
        this.errorTracking.captureException(error);

        // Pace-only refetch keeps the chart and nothing else reloads — toast so
        // the user sees the failure.
        if (this.riSelections.isPaceOnlyLoad) {
          this.toast.error(this.intl.t('general.error'), { showIcon: true });
        }

        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  getPickupDataTask = task(
    { restartable: true },
    async ({
      params,
      unfilteredQueryParams,
      filteredQueryParams,
    }: DataTaskParams) => {
      try {
        const currentHotelId = this.hotelAndMarketService.hotel.hotelinfo.id;
        // Check if cached response exists and belongs to the currently selected hotel
        const isCacheValid =
          Boolean(this.storedPickupUnfilteredResponse?.cachedResponse) &&
          this.storedPickupUnfilteredResponse?.hotelInfoId === currentHotelId;

        // Fetch unfiltered request if needed
        const unfilteredRequest =
          isCacheValid && this.storedPickupUnfilteredResponse
            ? this.storedPickupUnfilteredResponse.cachedResponse
            : this.fetchPickupDataTask.perform(unfilteredQueryParams);

        // Fetch filtered request if needed
        const filteredRequest = params.segmentationFilters.length
          ? this.fetchPickupDataTask.perform(filteredQueryParams)
          : undefined;

        const [unfilteredResponse, filteredResponse] = await all([
          unfilteredRequest,
          filteredRequest,
        ]);

        // Store unfiltered response if needed
        if (!isCacheValid) {
          this.storedPickupUnfilteredResponse = {
            hotelInfoId: this.hotelAndMarketService.hotel.hotelinfo.id!,
            cachedResponse: unfilteredResponse,
          };
        }

        return this.handleUnfilteredAndFilteredRequest({
          unfilteredRequest: unfilteredResponse,
          filteredRequest: filteredResponse,
          isCached: isCacheValid,
          segmentationType: SEGMENTATION_TYPES.PICKUP,
        }) as ReviApiResponseLike;
      } catch (error) {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        handleRequestError(error, params, this);
        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  getRatesDataTask = task(
    { restartable: true },
    async (queryParams: Params): Promise<Period[]> => {
      const params = cloneDeep(queryParams);

      // Only attempt to fetch rates data when there is a permission to do so in feature flags
      if (
        !isRatesAvailable(
          params.fromDate,
          params.toDate,
          this.hotelAndMarketService,
          params.segmentationFilters,
          params.stayDateFilter,
          null, // isGroupsSelected - does not apply here
          this.access.hasRates,
        )
      ) {
        return [];
      }

      if (this.storedRatesData) {
        return this.storedRatesData;
      }

      const { dateRangeStart, dateRangeEnd, stayDateFilterOffset } =
        getDateRange(
          params.fromDate,
          params.toDate,
          this.hotelAndMarketService,
          params.stayDateFilter,
        );

      // Take the stay date filter offset into account for the reference period
      if (stayDateFilterOffset > 0) {
        params.refFromDate = this.hotelAndMarketService
          .toLocalTime(params.refFromDate)
          .add(stayDateFilterOffset, 'day')
          .format('YYYY-MM-DD');
      }

      const dateRangeStartMoment =
        this.hotelAndMarketService.toLocalTime(dateRangeStart);
      const dateRangeEndMoment =
        this.hotelAndMarketService.toLocalTime(dateRangeEnd);

      const requestDays: Record<string, unknown> = {
        from_date_range_start: dateRangeStartMoment.format('YYYY-MM-DD'),
        from_date_range_end: dateRangeEndMoment.format('YYYY-MM-DD'),
        hotel_id: this.hotelAndMarketService.hotel?.id,
        typeKey: 'day',
        currency: this.riSelections.riCurrency,
      };

      if (params.profile) {
        requestDays['profile'] =
          this.currencyConversion.selectedProfileId ?? params.profile;
      }

      const requestPeriods: Record<string, unknown> = cloneDeep(requestDays);

      // Request new periods
      setProperties(requestPeriods, {
        namespace: APIGATEWAY,
        typeKey: 'rates',
        ota: this.hotelAndMarketService.hotel?.OTAsRates?.filter(
          (ota) => ota.isConnectedOrBrandDotCom,
        )?.[0]?.id,
        los: this.hotelAndMarketService.hotel?.defaultLosRates?.[0],
        mealtype: 0,
        persons: 2,
        roomtype: 'all',
        bar: true,
        membershiptype: 0,
        compset_ids: 1,
        meta: 'nested',
      });

      try {
        const ratesResponse = await this.store.query<Period>(
          'period',
          // @ts-expect-error: Incorrect type
          requestPeriods,
        );
        this.storedRatesData = ratesResponse;
        return ratesResponse;
      } catch (error) {
        this.errorTracking.captureMessage('Rates data fetch in RI failed!', {
          error,
        });
        this.storedRatesData = null;
        // Return empty response in case of error
        return [];
      }
    },
  );

  hasBudgetForecastDataTask = task(
    { restartable: true },
    async (params: Params) => {
      const { dateRangeStart, dateRangeEnd } = getDateRange(
        params.fromDate,
        params.toDate,
        this.hotelAndMarketService,
        params.stayDateFilter,
      );

      try {
        const requestQueryParams = {
          subscription_id: this.hotelAndMarketService.hotel.id!,
          start_date: dateRangeStart,
          end_date: dateRangeEnd,
        };

        const requestBudgetUrl = formatUrl(
          '/apigateway/v1/app/budgetforecast/budget/exists',
          requestQueryParams,
          this.ajax.host,
        );

        const requestForecastUrl = formatUrl(
          '/apigateway/v1/app/budgetforecast/forecast/exists',
          requestQueryParams,
          this.ajax.host,
        );

        const [budgetResponse, forecastResponse] = await Promise.all([
          this.ajax.request<{ exists: boolean }>(requestBudgetUrl),
          this.ajax.request<{ exists: boolean }>(requestForecastUrl),
        ]);

        return {
          hasBudgetData: budgetResponse.exists,
          hasForecastData: forecastResponse.exists,
        };
      } catch (error) {
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  requestEventsTask = task(
    { restartable: true },
    async (queryParams: Record<string, unknown>) => {
      try {
        // @ts-expect-error: Incorrect type
        const daysResponse = await this.store.query<Day>('day', queryParams);
        return daysResponse;
      } catch (error) {
        this.errorTracking.captureMessage('Events data fetch in RI failed!', {
          error,
        });
        // Return empty response in case of error
        return [];
      }
    },
  );

  requestHolidaysTask = task(
    { restartable: true },
    async (queryParams: Record<string, unknown>) => {
      try {
        const holidaysResponse = await this.store.query<Day>(
          'day',
          // @ts-expect-error: Incorrect type
          queryParams,
        );
        return holidaysResponse;
      } catch (error) {
        this.errorTracking.captureMessage('Holidays data fetch in RI failed!', {
          error,
        });
        // Return empty response in case of error
        return [];
      }
    },
  );

  requestRatesTask = task(
    { restartable: true },
    async (queryParams: Record<string, unknown>) => {
      try {
        const ratesResponse = await this.store.query<Period>(
          'period',
          // @ts-expect-error: Incorrect type
          queryParams,
        );
        return ratesResponse;
      } catch (error) {
        this.errorTracking.captureMessage('Rates data fetch in RI failed!', {
          error,
        });
        // Return empty response in case of error
        return [];
      }
    },
  );

  get breakdownsToFetch(): RiBreakdown[] {
    return [
      RI_BREAKDOWNS.ALL,
      RI_BREAKDOWNS.PACE,
      RI_BREAKDOWNS.PICKUP,
      RI_BREAKDOWNS.IMPACT_DATE,
      ...this.hotelAndMarketService.hotel.revenueInsightSupportedAggregations,
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get defaultSegmentationObjects() {
    const defaultSegmentationObjects = this.breakdownsToFetch.reduce(
      (accumulator, breakdown) => {
        accumulator[breakdown] = [];

        return accumulator;
      },
      {} as Record<RiBreakdown, never[]> & { isLoading: boolean },
    );

    defaultSegmentationObjects.isLoading = true;

    return defaultSegmentationObjects;
  }

  activate(transition: Transition): void {
    super.activate(transition);

    this.riSelections.isMultiProperty = false;
  }

  beforeModel(transition: Transition): void {
    super.beforeModel(transition);

    if (!this.access.hasFeature('app.revenue_insight')) {
      this.transition.redirectToEnabledTab(transition);
    } else {
      this.transition.validateRevenueInsightQueryParams(transition);
      this.checkFilterUpdate(transition);
    }
  }

  checkFilterUpdate(transition: Transition): void {
    const { from, to } = transition;

    if (!from || !to) {
      return;
    }

    if (areQueryParamsEqual(from.queryParams, to.queryParams)) {
      return;
    }

    this.clearCache();
  }

  clearCache(): void {
    this.storedBreakdownUnfilteredResponse = null;
    this.storedKpiUnfilteredResponse = null;
    this.storedPaceUnfilteredResponse = null;
    this.storedPickupUnfilteredResponse = null;
    this.storedRatesData = null;
    this.storedUnfilteredSegmentationDict = {
      kpi: {},
      breakdown: {},
      pace: {},
      pickup: {},
    };
    this.unfilteredRequestHadBeenMappedBefore = {
      kpi: false,
      breakdown: false,
      pace: false,
      pickup: false,
    };

    /**
     * In application tests, the app gets torn down after each test. This causes the store to be destroyed
     * before the deactivate hook is called. This check is to prevent the store from being accessed after it's destroyed.
     */
    if (this.store.isDestroyed) {
      return;
    }

    this.store.unloadAll('revi-multi-property-api-response');
    this.store.unloadAll('revi-exchange-rate');
    this.store.unloadAll('revi-api-response');
    this.store.unloadAll('revenue-insight/kpis-request');
    this.store.unloadAll('revenue-insight/breakdowns-request');
    this.store.unloadAll('revenue-insight/segmentation-value');
    this.store.unloadAll('revenue-insight/segmentation-data');
  }

  clearFilters(controller: HotelRevenueInsightController): void {
    controller.segmentationFilters = '';
    controller.stayDateFilter = '';
  }

  deactivate(transition: Transition): void {
    super.deactivate(transition);
    // Cancel all possibly running tasks. This will make sure the request is not done anymore if it was waiting
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.requestRatesTask.cancelAll();
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.requestEventsTask.cancelAll();
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.requestHolidaysTask.cancelAll();

    void this.getBreakdownDataTask.cancelAll();
    void this.getKpiDataTask.cancelAll();
    void this.getPaceDataTask.cancelAll();
    void this.getPickupDataTask.cancelAll();

    void this.getRatesDataTask.cancelAll();
    void this.hasBudgetForecastDataTask.cancelAll();
    void this.getFavouritesDataTask.cancelAll();
    void this.getEventsDataTask.cancelAll();
    void this.getHolidaysDataTask.cancelAll();

    /**
     * In application tests, the app gets torn down after each test. This causes the store to be destroyed
     * before the deactivate hook is called. This check is to prevent the store from being accessed after it's destroyed.
     */
    if (!this.store.isDestroyed) {
      // Clear rate data from store
      this.store.unloadAll('event');
      this.store.unloadAll('holiday');
    }

    this.favourites = null;
  }

  /**
   * Generates query parameters for the days request.
   * @param params Route parameters.
   * @returns Object with query parameters for the days request.
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  generateDaysQueryParameters(params: Params) {
    const { dateRangeStart, dateRangeEnd } = getDateRange(
      params.fromDate,
      params.toDate,
      this.hotelAndMarketService,
      params.stayDateFilter,
    );
    const dateRangeStartMoment =
      this.hotelAndMarketService.toLocalTime(dateRangeStart);
    const dateRangeEndMoment =
      this.hotelAndMarketService.toLocalTime(dateRangeEnd);

    return {
      namespace: APIGATEWAY,
      subscription_id: this.hotelAndMarketService.hotel?.id,
      from_date_range_start: dateRangeStartMoment.format('YYYY-MM-DD'),
      from_date_range_end: dateRangeEndMoment.format('YYYY-MM-DD'),
    };
  }

  /**
   * Generates query parameters for unfiltered and filtered segmentation requests.
   * @param params Route parameters.
   * @returns Object with unfilteredQueryParams and filteredQueryParams.
   */
  generateSegmentationQueryParameters(params: Params): {
    filteredQueryParams: Record<string, unknown>;
    unfilteredQueryParams: Record<string, unknown>;
  } {
    const { dateRangeStart, dateRangeEnd, stayDateFilterOffset } = getDateRange(
      params.fromDate,
      params.toDate,
      this.hotelAndMarketService,
      params.stayDateFilter,
    );

    // Take the stay date filter offset into account for the reference period
    let refFromDate = params.refFromDate;
    if (stayDateFilterOffset > 0) {
      refFromDate = this.hotelAndMarketService
        .toLocalTime(params.refFromDate)
        .add(stayDateFilterOffset, 'day')
        .format('YYYY-MM-DD');
    }

    // Get extract date from QP if is admin
    const extractDate = getExtractDate(
      params.extractDate,
      this.access.isStaffUser,
    );

    const segmentationCategoryMappings =
      this.hotelAndMarketService.user?.segmentationCategoryMappings;

    const reference = isAPIReference(params.reference)
      ? params.reference
      : refFromDate;

    const isBudgetOrForecast =
      reference === 'budget' || reference === 'forecast';

    const unfilteredQueryParams: Record<string, unknown> = {
      namespace: APIGATEWAY,
      subscription_id: this.hotelAndMarketService.hotel?.id,
      start_date: dateRangeStart,
      end_date: dateRangeEnd,
      ref_period: reference,
      'aggregations[]': this.breakdownsToFetch,
      'category_aggregations[]': segmentationCategoryMappings,
      disable_pickup_ly: isBudgetOrForecast
        ? Boolean(params.hidePickupLY)
        : !params.showPickupRealised,
      kpi_category: params.kpiCategory,
      currency: this.riSelections.riCurrency,
    };

    if (params.profile) {
      unfilteredQueryParams['profile'] =
        this.currencyConversion.selectedProfileId ?? params.profile;
    }

    if (params.disableDataContinuity === true) {
      unfilteredQueryParams['disable_data_continuity'] = true;
    }

    if (extractDate) {
      unfilteredQueryParams['extract_date'] = extractDate;
    }

    const filteredQueryParams: Record<string, unknown> = {
      ...unfilteredQueryParams,
      filters: params.segmentationFilters,
    };
    return { unfilteredQueryParams, filteredQueryParams };
  }

  /**
   * Handles both unfiltered and filtered requests, processes the data, and returns the final mapped segmentation items.
   * @param unfilteredRequest The API response for the unfiltered request.
   * @param filteredRequest The API response for the filtered request (optional).
   * @param isCached Indicates if the unfiltered request was cached.
   * @returns The final mapped segmentation items.
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  handleUnfilteredAndFilteredRequest({
    unfilteredRequest,
    filteredRequest,
    isCached,
    segmentationType,
  }: {
    filteredRequest?: ReviApiResponseLike;
    isCached: boolean;
    segmentationType: SegmentationType;
    unfilteredRequest: ReviApiResponseLike;
  }) {
    let unfilteredSegmentationDict: SegmentationDict = {};
    let filteredSegmentationDict: SegmentationDict = {};

    if (!isCached) {
      unfilteredSegmentationDict =
        this.makeUnfilteredSegmentationDict(unfilteredRequest);
      this.storedUnfilteredSegmentationDict[segmentationType] =
        unfilteredSegmentationDict;
    }
    unfilteredSegmentationDict =
      this.storedUnfilteredSegmentationDict[segmentationType];

    const hasFilter = !!filteredRequest;

    if (hasFilter) {
      filteredSegmentationDict = this.makeFilteredSegmentationDict(
        filteredRequest,
        unfilteredRequest,
        unfilteredSegmentationDict,
      );
    }

    const finalResult = this.mapSegmentationItems(
      hasFilter,
      segmentationType,
      unfilteredSegmentationDict,
      filteredSegmentationDict,
    );

    // If there is no filter, the unfiltered request has just been mapped. Save it for later
    if (!hasFilter) {
      this.unfilteredRequestHadBeenMappedBefore[segmentationType] = true;
    }

    return finalResult;
  }

  /**
   * Generates a filtered segmentation dictionary from the API response and the unfiltered segmentation dictionary.
   * @param { ReviApiResponseLike } filteredResponse : the API response containing filtered segmentation data
   * @param { ReviApiResponseLike } unfilteredResponse : the API response containing unfiltered segmentation data
   * @param { SegmentationDict } unfilteredSegmentationDict : the unfiltered segmentation dictionary
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  makeFilteredSegmentationDict(
    filteredResponse: ReviApiResponseLike,
    unfilteredResponse: ReviApiResponseLike,
    unfilteredSegmentationDict: SegmentationDict,
  ) {
    const filteredSegmentationDict: SegmentationDict = {};

    const isFuture = this.riSelections.isToDateInFuture;
    const demoMode = getDemoModeCookie();

    // Loop over every key in de model (travel_agent, all, ...)
    const { keys } = filteredResponse;

    keys.forEach((key) => {
      filteredSegmentationDict[key] = {};

      const segmentationIdValidations = getSegmentationIdValidations(key);

      filteredResponse[key].forEach((filteredItem) => {
        const unfilteredItem =
          unfilteredSegmentationDict?.[key]?.[filteredItem.unfilteredId];
        if (unfilteredItem) {
          filteredItem.unfiltered_data = unfilteredItem;
          // All of these values are exactly the same as for the unfiltered one
          // No need to calculate them again
          filteredItem.displayValue = unfilteredItem.displayValue;
          filteredItem.segmentationSortValue =
            unfilteredItem.segmentationSortValue;
          filteredItem.pickupCount = unfilteredItem.pickupCount;
          filteredItem.isSelectable = unfilteredItem.isSelectable;
        } else {
          // If there is no unfiltered data, we need to the same for this as when we receive unfiltered data
          // This can happen for the pickup block, or if the values are outside of the top 200 in RIMP
          this.mapNewSegmentationItem(
            filteredItem,
            segmentationIdValidations,
            isFuture,
            demoMode,
          );
        }

        if (filteredSegmentationDict[key]) {
          filteredSegmentationDict[key][filteredItem.unfilteredId] =
            filteredItem;
        }
      });

      // No need for sorting on unfiltered values for these two breakdowns
      if (segmentationIdValidations.isKeyNotPickupAndPace) {
        unfilteredResponse[key].forEach((unfilteredItem) => {
          addDummyElementToFilteredSegmentationDict(
            filteredSegmentationDict,
            unfilteredItem,
          );
        });
      }
    });

    return filteredSegmentationDict;
  }

  /**
   * Generates an unfiltered segmentation dictionary from the API response.
   * @param { ReviApiResponseLike } response : the API response containing unfiltered segmentation data
   * @returns { SegmentationDict } : a dictionary mapping breakdown types to their respective segmentation items
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  makeUnfilteredSegmentationDict(response: ReviApiResponseLike) {
    const unfilteredSegmentationDict: SegmentationDict = {};

    const isFuture = this.riSelections.isToDateInFuture;
    const demoMode = getDemoModeCookie();

    const { keys } = response;
    keys.forEach((key) => {
      const segmentationIdValidations = getSegmentationIdValidations(key);

      unfilteredSegmentationDict[key] = {};

      response[key].forEach((item) => {
        this.mapNewSegmentationItem(
          item,
          segmentationIdValidations,
          isFuture,
          demoMode,
        );

        if (unfilteredSegmentationDict[key]) {
          // @ts-expect-error: Type 'null' cannot be used as an index type.
          unfilteredSegmentationDict[key][item.id] = item;
        }
      });
    });

    return unfilteredSegmentationDict;
  }

  mapNewSegmentationItem(
    item: RevenueInsightSegmentationValue,
    segmentationIdValidations: ReturnType<typeof getSegmentationIdValidations>,
    isFuture: boolean,
    demoMode: DemoModeObject | null,
  ): void {
    const hasNoValueAndNoReferenceValue =
      item.value === null && item.referenceValue === null;

    // segmentationSortValue is different for pickup segmentations, handled in separate if
    if (segmentationIdValidations.isKeyNotPickupAndWeekday) {
      item.segmentationSortValue = item.segmentationValue;
    }

    // Translating the weekdays from 1 - 7 to actual days
    if (segmentationIdValidations.isKeyWeekday) {
      item.segmentationSortValue = item.segmentationValueId;
    }

    if (
      segmentationIdValidations.isKeyPickup &&
      item.segmentationValue.indexOf(
        `revenue_insight.${RI_BREAKDOWNS.PICKUP}`,
      ) !== -1
    ) {
      const splittedSegmentationValue = item.segmentationValue.split('.');
      const xValue = splittedSegmentationValue[2];
      item.pickupCount = xValue;
    }

    if (
      item.segmentationValue.includes('revenue_insight.unaccounted_group') ||
      item.segmentationValue.includes(
        'revenue_insight.unaccounted_transient',
      ) ||
      item.segmentationValue.includes('revenue_insight.left_to_map')
    ) {
      item.isSelectable = false;
    } else {
      item.isSelectable = true;
    }

    const hasInvalidValueAndInvalidReferenceValue =
      item.value?.message === INVALID_RESERVATIONS_MESSAGE &&
      item.referenceValue?.message === INVALID_RESERVATIONS_MESSAGE;

    if (
      segmentationIdValidations.isKeyPickup &&
      (hasNoValueAndNoReferenceValue || hasInvalidValueAndInvalidReferenceValue)
    ) {
      item.isDisabled = true;
    }

    const subscriptionName = item.meta?.name;

    // We only format put the displayValue for breakdowns that can be searched
    // Since pace and stay date cannot be searched, no need for a display value
    // This also makes sure we don't use the moment format in this route which save ~100ms
    if (segmentationIdValidations.isKeyNotPaceAndStayDate) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      item.displayValue = segmentationValueFormatter(
        item.segmentationId,
        item.segmentationValue,
        {
          // Should be no past dates, today is not a past date
          isFuture,
          subscriptionName,
          intl: this.intl,
          demoMode,
        },
      );
    }
  }

  /**
   * Maps segmentation items into a structured format, organizing them by their breakdown type.
   * @param { boolean } hasFilter : indicates if a filter is applied
   * @param { object } unfilteredSegmentationDict : the unfiltered request segmentation dictionary
   * @param { object } filteredSegmentationDict : the filtered request segmentation dictionary
   * @returns { object } : a dictionary mapping breakdown types to their respective segmentation items
   * */
  mapSegmentationItems(
    hasFilter = false,
    segmentationType: SegmentationType,
    unfilteredSegmentationDict: SegmentationDict,
    filteredSegmentationDict: SegmentationDict,
  ): {
    [key in RiBreakdown]?: SegmentationValueLike[];
  } {
    const reservations: {
      [key in RiBreakdown]?: SegmentationValueLike[];
    } = {};

    // determine where you need to get the values from
    const segmentationDict = hasFilter
      ? filteredSegmentationDict
      : unfilteredSegmentationDict;

    if (!segmentationDict) {
      return {};
    }

    // If it's already been mapped before, no need to map the children again
    // We already saved the mapping in the dict
    const shouldMap =
      hasFilter || !this.unfilteredRequestHadBeenMappedBefore[segmentationType];

    const keys = Object.keys(
      segmentationDict,
    ) as (keyof typeof segmentationDict)[];

    if (shouldMap) {
      // Reset children properties from the previous mapping
      keys.forEach((segmentationId) => {
        if (segmentationDict[segmentationId]) {
          Object.keys(segmentationDict[segmentationId]).forEach(
            (segmentationValueId) => {
              const segmentationItem =
                segmentationDict[segmentationId]?.[segmentationValueId];
              if (
                segmentationItem &&
                'children' in segmentationItem &&
                segmentationItem?.children?.length
              ) {
                set(segmentationItem, 'children', []);
              }
            },
          );
        }
      });
    }

    keys.forEach((segmentName) => {
      reservations[segmentName] = [];
      // Always loop over the unfiltered request. Even though a filter is active, we always show all items of a breakdown
      if (segmentationDict[segmentName]) {
        Object.keys(segmentationDict[segmentName]).forEach(
          (segmentationItemId) => {
            const reservationData =
              segmentationDict[segmentName]?.[segmentationItemId];

            // If pickup is filtered, pace is filtered as well
            // We do not add dummy values for those two so we need to make sure they are in the dict
            if (reservationData) {
              if (shouldMap && reservationData.parentId) {
                const parent =
                  segmentationDict[segmentName]?.[reservationData.parentId];

                if (parent) {
                  // set default
                  if (!parent.children) {
                    parent.children = [];
                  }

                  // hang this child under the parent
                  parent.children.push(
                    reservationData as SegmentationValueLike,
                  );
                  // and a link the other way around to the parent from here
                  reservationData.parent = parent;
                }
              }
              // Only add root elements to the model
              if (reservationData.parentId === null) {
                reservations[segmentName]?.push(
                  reservationData as SegmentationValueLike,
                );
              }
            }
          },
        );
      }
    });

    return reservations;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  model(params: Params) {
    // Model-wide load: clear the pace-only flag so the skeleton loader shows, not a stale chart.
    this.riSelections.isPaceOnlyLoad = false;

    const model: Model = {
      hasBudgetForecastTask: this.hasBudgetForecastDataTask,
      kpis: this.getKpiDataTask,
      favourites: this.getFavouritesDataTask,
      rates: this.getRatesDataTask,
      events: this.getEventsDataTask,
      holidays: this.getHolidaysDataTask,
      pace: this.getPaceDataTask,
      pickup: this.getPickupDataTask,
      breakdowns: this.getBreakdownDataTask,
    };

    void model.hasBudgetForecastTask.perform(params);

    this.updateLastActiveHotel();
    void model.favourites.perform();

    const dayQueryParams = this.generateDaysQueryParameters(params);
    void model.events.perform(dayQueryParams);
    void model.holidays.perform(dayQueryParams);

    const segmentationParams = {
      params,
      ...this.generateSegmentationQueryParameters(params),
    };

    void model.pace.perform(segmentationParams);
    void model.pickup.perform(segmentationParams);
    void model.breakdowns.perform(segmentationParams);

    void model.rates.perform(params);
    void model.kpis.perform(segmentationParams);

    if (params.profile) {
      this.currencyConversion.setSelectedProfileId(params.profile);
    }

    return model;
  }

  /**
   * Re-performs only getPaceDataTask (model.pace) for a leadtime / pickup-realised
   * change, instead of reloading the whole model. Caller must pass the live values:
   * the controller sets these refreshModel:false QPs synchronously just before
   * calling, and paramsFor still returns the previous values at that point.
   */
  refetchPace(leadtime: number, showPickupRealised: boolean): void {
    const params = {
      ...(this.paramsFor(this.routeName) as Params),
      leadtime,
      showPickupRealised,
    };

    // Pace-only refetch: keep the previous chart on screen while the new data
    // loads (stale-while-revalidate), instead of blanking to the skeleton.
    this.riSelections.isPaceOnlyLoad = true;

    // Cache is hotel-keyed only, so bust it for the new params to be requested.
    this.storedPaceUnfilteredResponse = null;

    const segmentationParams = {
      params,
      ...this.generateSegmentationQueryParameters(params),
    };

    void this.getPaceDataTask.perform(segmentationParams);
  }

  // @ts-expect-error: Incorrect type

  resetController(
    controller: HotelRevenueInsightController,
    isExiting: boolean,
  ): void {
    if (isExiting) {
      // Clear segmentationFilters & cached data when the route is exiting
      this.clearFilters(controller);
      this.clearCache();

      /**
       * For demo properties
       * We need to reset the QPs back to the "normal" defaults
       * Unsure why, but only doing it in the willTransition did not seem to work, which is why we also reset the controller properties
       */
      if (this.hotelAndMarketService.hotel.isDemoProperty) {
        setProperties(controller, {
          extractDate: null,
          fromDate: null,
          reference: 'SWLY',
          refFromDate: null,
          stayDateFilter: '',
          toDate: null,
        });
      }
    }
  }

  /**
   * This function sets the required riSelections properties from the API response.
   * @param { object } request : the request object
   */

  setRiSelectionsPropertiesFromRequest(request: ReviApiResponseLike): void {
    const allSegmentationMeta = request.all?.[0]?.meta;

    if (!allSegmentationMeta) {
      return;
    }

    this.riSelections.availableKpiCategories =
      allSegmentationMeta.kpi_categories;
    this.riSelections.summaryOverwrite = allSegmentationMeta.uses_summary;
    this.riSelections.isNetComparison = allSegmentationMeta.is_net_comparison;
  }

  // @ts-expect-error: Incorrect type

  setupController(controller: HotelRevenueInsightController): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line prefer-rest-params
    super.setupController(...arguments);

    // Hand the controller a typed reference to this route (it can't inject one)
    // so user actions can trigger a pace-only refetch.
    controller.route = this;

    controller.validateMissingAndUnavailableBlocks();
  }

  /**
   * Keeps track of the last active hotel and clears cached data when the hotel changes
   */

  updateLastActiveHotel(): void {
    if (!this.lastActiveHotelId) {
      this.lastActiveHotelId =
        this.hotelAndMarketService.hotel?.hotelinfo?.id ?? null;
      return;
    }

    if (
      this.lastActiveHotelId !== this.hotelAndMarketService.hotel?.hotelinfo?.id
    ) {
      this.lastActiveHotelId =
        this.hotelAndMarketService.hotel?.hotelinfo?.id ?? null;

      // Make sure we remove cached data when changing hotels
      this.clearCache();
      this.favourites = null;
    }
  }

  /**
   * Validates the selected KPI category against the available categories.
   * If the selected category is not available, it resets the selection to a default value - TOTAL.
   * @param { KpiCategory } kpiCategory - The selected KPI category to validate.
   */

  validateKpiCategory(kpiCategory: KpiCategory): void {
    // If there are no available kpi categories, we don't need to check anything
    // This can happen when the API is not yet loaded or failed
    if (this.riSelections.availableKpiCategories.length === 0) {
      return;
    }

    // Check that the selected kpi category is included in the list of kpi categories that we get from API.
    if (!this.riSelections.availableKpiCategories.includes(kpiCategory)) {
      this.router.transitionTo({
        queryParams: { kpiCategory: KPI_CATEGORIES.TOTAL },
      });
    }
  }

  @action doRefresh(): void {
    this.clearCache();
    this.refresh();
  }

  @action willTransition(transition: Transition): void {
    /**
     * For demo properties
     * We need to reset the QPs back to the "normal" defaults
     */
    if (
      this.hotelAndMarketService.hotel.isDemoProperty &&
      !this.hasAbortedDemoTransition
    ) {
      const transitionToHotelId = // @ts-expect-error: 'transition.to' is possibly 'undefined'.
        (transition.to.find((route) => route.name === 'hotel') as RouteInfo)
          .params['id'] as string;

      this.hasAbortedDemoTransition = true;
      transition.abort();
      this.router.transitionTo(transition.to.name, transitionToHotelId, {
        queryParams: {
          fromDate: null,
          toDate: null,
          refFromDate: null,
          reference: 'SWLY',
          stayDateFilter: '',
          extractDate: null,
        },
      });
    }
  }
}
